import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AxiosInstance } from 'axios';

import useCompleteLogin from './api/useCompleteLogin';
import { AppType, AuthState, IOAuth2Config } from '../utils/types';
import useAuth from './useAuth';
import { useMessage } from '../../hooks/useMessage';
import { validateOrgAccess } from '../utils/validateOrgAccess';
import { clearAuthStorage } from '../utils/clearAuthStorage';
import { consumeReturnTo } from '../utils/returnTo';
import useLogout from './api/useLogout';

interface IUseAuthCallbackProps {
  config: IOAuth2Config;
  axiosInstance: AxiosInstance;
  setToken: (token: string | undefined) => void;
  dashboardPath: string;
  loginPath: string;
  appType: AppType;
  noAuthorizedRedirectUrl: string;
  onError?: (error: unknown) => void;
}

const useAuthCallback = ({
  config,
  axiosInstance,
  setToken,
  dashboardPath,
  loginPath,
  appType,
  noAuthorizedRedirectUrl,
  onError,
}: IUseAuthCallbackProps) => {
  const navigate = useNavigate();
  const { setUserToken } = useAuth();
  const { logout: serverLogout } = useLogout();
  const message = useMessage();
  const { completeLogin, isCompletingLogin, completeLoginError, data, status } =
    useCompleteLogin({
      config,
      axiosInstance,
      setToken,
      onError: onError
        ? onError
        : e => {
            const errMsg =
              e instanceof Error ? e.message : 'Error during login';
            message.error(errMsg);
          },
    });

  const handleNoAccess = (accessToken: string) => {
    serverLogout();
    clearAuthStorage(accessToken);
    // The remembered location belongs to the app this user cannot use.
    consumeReturnTo();

    const errorMessage =
      appType === AppType.ADMIN
        ? 'You do not have access to the Admin application. Redirecting to Client application.'
        : 'You do not have access to the Client application. Redirecting to Admin application';

    alert(errorMessage);

    const url = new URL(noAuthorizedRedirectUrl);
    url.pathname = '';
    url.search = '';
    url.hash = '';
    window.location.href = url.toString();
  };

  useEffect(() => {
    if (status === 'idle') {
      completeLogin(undefined, {
        onSuccess: ({ accessToken, refreshToken }) => {
          const hasAccess = validateOrgAccess(accessToken, appType);

          if (!hasAccess) {
            handleNoAccess(accessToken);
            return;
          }

          setUserToken({ accessToken, refreshToken });

          // Return the user to whatever they were doing when the session ran out, rather than
          // dropping them on the dashboard.
          navigate(consumeReturnTo() ?? dashboardPath, { replace: true });
        },
      });
    }
  }, [
    completeLogin,
    navigate,
    setUserToken,
    status,
    dashboardPath,
    appType,
    serverLogout,
    noAuthorizedRedirectUrl,
  ]);

  const authState: AuthState = useMemo(() => {
    if (isCompletingLogin) return 'loading';
    if (completeLoginError && status === 'error') return 'error';
    if (data?.accessToken || status === 'success') return 'success';
    return 'loading';
  }, [isCompletingLogin, completeLoginError, status, data]);

  const errorMessage =
    completeLoginError instanceof Error ? completeLoginError.message : '';

  const handleTryAgain = () => {
    navigate(loginPath, { replace: true });
  };

  return {
    authState,
    errorMessage,
    handleTryAgain,
  };
};

export default useAuthCallback;
